'use client';

import { useCallback } from 'react';
import { useEnterLogin, type EnterLoginOptions } from '@enter/auth';
import { useMixpanel } from '@/contexts/mixpanel';
import {
    appendEnterHomeAuthAnalytics,
    createEnterHomeAuthAttempt,
    createEnterHomeAuthAuthorizationParams,
    createEnterHomeAuthTelemetry,
    takePendingEnterHomeAuthAttempt,
    type EnterHomeAuthAttempt,
    type EnterAuthEntry,
    type EnterLoginIntent,
    type EnterLoginType,
} from '@/contexts/auth/login-analytics';

export interface EnterHomeLoginOptions extends EnterLoginOptions {
    /** 本次登录尝试已创建的链路上下文；入口点击事件与 Redirect 必须复用同一个 trace。 */
    analyticsAttempt?: EnterHomeAuthAttempt;
    /** 未提前创建链路上下文时使用的稳定入口。 */
    authEntry?: EnterAuthEntry;
    /** 登录或注册意图，不代表最终是否创建新用户。 */
    loginIntent?: EnterLoginIntent;
    /** 权威登录路径类型。 */
    loginType?: EnterLoginType;
}

/** Enter Home 用户主动触发的统一 Universal Login Redirect 入口。 */
export function useEnterHomeLogin() {
    const { loginWithRedirect } = useEnterLogin();
    const { getDistinctId, logger } = useMixpanel();

    const login = useCallback(
        (options?: EnterHomeLoginOptions) => {
            const {
                analyticsAttempt,
                authEntry = 'direct_link',
                loginIntent = options?.authorizationParams?.screen_hint === 'signup' ? 'signup' : 'login',
                loginType = 'standard',
                ...loginOptions
            } = options ?? {};
            const pendingAttempt = takePendingEnterHomeAuthAttempt();
            const existingAttempt = analyticsAttempt ?? pendingAttempt;
            const attempt = existingAttempt
                ? {
                      ...existingAttempt,
                      mixpanelDistinctId: existingAttempt.mixpanelDistinctId ?? getDistinctId(),
                  }
                : createEnterHomeAuthAttempt({
                      entry: authEntry,
                      loginIntent,
                      loginType,
                      mixpanelDistinctId: getDistinctId(),
                      authTraceId: loginOptions.authTraceId,
                      persist: false,
                  });
            const onTelemetry = createEnterHomeAuthTelemetry(attempt, logger);
            const fallbackReturnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            const returnTo = appendEnterHomeAuthAnalytics(
                loginOptions.appState?.returnTo || fallbackReturnTo,
                attempt,
            );

            onTelemetry('auth_stage_start', 'login');
            return loginWithRedirect({
                ...loginOptions,
                authorizationParams: {
                    ...loginOptions.authorizationParams,
                    ...createEnterHomeAuthAuthorizationParams(attempt),
                },
                appState: {
                    ...loginOptions.appState,
                    returnTo,
                },
                authTraceId: attempt.authTraceId,
                onTelemetry: (eventName, stage) => {
                    onTelemetry(eventName, stage);
                    loginOptions.onTelemetry?.(eventName, stage);
                },
            });
        },
        [getDistinctId, logger, loginWithRedirect]
    );

    return { login };
}
